'use client';

import { useState } from 'react';
import { Bell } from 'lucide-react';

export function EmailCapture() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, sourcePath: window.location.pathname }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.ok) {
        throw new Error('Newsletter subscription failed');
      }
      setSubmitted(true);
    } catch {
      setError('We couldn\'t save your email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Spec info panel (design-spec.md §5/§10): slate panel, bordered white input, secondary button. No gradient.
  if (submitted) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5" role="status">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Bell className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          You&apos;re on the list!
        </h3>
        <p className="mt-1 text-sm text-slate-700">We&apos;ll notify you when new Matter devices get certified.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Bell className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
        Stay updated
      </h3>
      <p className="mt-1 text-sm text-slate-700">Get notified when new Matter-certified devices launch.</p>
      <form onSubmit={handleSubmit} className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(''); }}
          placeholder="you@example.com"
          autoComplete="email"
          aria-label="Email address"
          aria-invalid={Boolean(error)}
          disabled={isSubmitting}
          className="min-h-11 flex-1 rounded-md border border-slate-300 bg-white px-3 text-base text-slate-900 placeholder:text-slate-500 sm:text-sm focus:border-blue-600 disabled:bg-slate-100"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-50 disabled:cursor-wait disabled:opacity-70"
        >
          {isSubmitting ? 'Saving…' : 'Notify me'}
        </button>
      </form>
      {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
      <p className="mt-2 text-xs text-slate-500">No spam. Unsubscribe anytime.</p>
    </div>
  );
}
